package com.hyperether.goodjob.scenes.profile

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.gestures.scrollBy
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.wrapContentWidth
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.selection.DisableSelection
import androidx.compose.material.Card
import androidx.compose.material.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.isShiftPressed
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.zIndex
import com.hyperether.goodjob.models.Plan
import com.hyperether.goodjob.models.Role
import com.hyperether.goodjob.models.User
import com.hyperether.goodjob.repository.remote.model.WorkingHours
import com.hyperether.goodjob.resources.Res
import com.hyperether.goodjob.resources.admin
import com.hyperether.goodjob.resources.check_circle_1
import com.hyperether.goodjob.resources.current_plan
import com.hyperether.goodjob.resources.email_address
import com.hyperether.goodjob.resources.employee
import com.hyperether.goodjob.resources.full_name
import com.hyperether.goodjob.resources.manager
import com.hyperether.goodjob.resources.managers
import com.hyperether.goodjob.resources.month
import com.hyperether.goodjob.resources.no_hours_available
import com.hyperether.goodjob.resources.personal_details
import com.hyperether.goodjob.resources.phone
import com.hyperether.goodjob.resources.upcoming_leaves
import com.hyperether.goodjob.resources.users
import com.hyperether.goodjob.resources.working_hours
import com.hyperether.goodjob.resources.you_chose
import com.hyperether.goodjob.scenes.components.CircleWithInitialUser
import com.hyperether.goodjob.scenes.components.ProfilePictureSection
import com.hyperether.goodjob.scenes.components.clickableWithPointer
import com.hyperether.goodjob.theme.BlueApp
import com.hyperether.goodjob.theme.PopularPricingText
import com.hyperether.goodjob.theme.SubtitleColor
import com.hyperether.goodjob.util.DateTimeUtil.formatWorkingHoursText
import com.hyperether.goodjob.util.DateTimeUtil.groupContiguousWorkingHours
import com.hyperether.goodjob.util.UserStatusHelper
import com.hyperether.goodjob.util.toScaleDp
import com.hyperether.goodjob.util.toScaleSp
import com.hyperether.resources.currentLanguage
import com.hyperether.resources.stringResource
import com.hyperether.resources.stringResourcePlain
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.painterResource
import kotlin.math.roundToInt

@Composable
fun ProfileHeaderView(user: User?) {
    val roleMap = mapOf(
        stringResource(Res.string.admin) to Role.admin.name,
        stringResource(Res.string.manager) to Role.manager.name,
        stringResource(Res.string.employee) to Role.employee.name,
    )

    Card(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 16.toScaleDp()),
        shape = RoundedCornerShape(8.toScaleDp()),
        elevation = 2.toScaleDp()
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.toScaleDp()),
            verticalAlignment = Alignment.CenterVertically
        ) {
            CircleWithInitialUser(user, size = 50.toScaleDp())

            Spacer(modifier = Modifier.width(10.toScaleDp()))

            Column(
                modifier = Modifier.weight(1f)
            ) {
                user?.getName()?.let {
                    Text(
                        text = it,
                        fontWeight = FontWeight.Bold,
                        fontSize = 14.toScaleSp()
                    )
                }
                user?.role?.let {
                    Text(
                        text = roleMap.entries.find { role ->
                            role.value.equals(
                                it,
                                ignoreCase = true
                            )
                        }?.key ?: "",
                        color = SubtitleColor,
                        fontSize = 14.toScaleSp(),
                        modifier = Modifier.offset(y = (-5).toScaleDp()),
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                }
            }

            Text(
                text = UserStatusHelper.getStatusText(
                    confirmStatus = user?.confirmStatus,
                    status = user?.status
                ),
                modifier = Modifier.align(Alignment.CenterVertically).offset(y = (-13).toScaleDp()),
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
        }
    }
}

@Composable
fun ProfileDetails(title: String, info: String) {
    Card(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 16.toScaleDp()),
        shape = RoundedCornerShape(8.toScaleDp()),
        elevation = 2.toScaleDp()
    ) {
        Column(modifier = Modifier.padding(16.toScaleDp())) {
            Text(
                text = title,
                fontWeight = FontWeight.Bold,
                fontSize = 16.toScaleSp(),
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
            Text(
                text = info,
                fontSize = 14.toScaleSp(),
                color = SubtitleColor,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
        }
    }
}


@Composable
fun ProfileSettingsWeb(user: User?) {
    val selectedIndex = remember { mutableStateOf(1) }

    val rolesMap = mapOf(
        stringResource(Res.string.admin) to Role.admin.name,
        stringResource(Res.string.manager) to Role.manager.name,
        stringResource(Res.string.employee) to Role.employee.name,
    )

    BoxWithConstraints(
        modifier = Modifier.fillMaxWidth()
    ) {
        val boxWidth = maxWidth

        val pictureSize = when {
            boxWidth < 500.toScaleDp() -> 100.toScaleDp()
            boxWidth < 700.toScaleDp() -> 140.toScaleDp()
            boxWidth < 900.toScaleDp() -> 180.toScaleDp()
            else -> 220.toScaleDp()
        }

        Row(modifier = Modifier.fillMaxWidth()) {
            Column(
                modifier = Modifier
                    .weight(4f)
                    .padding(end = 20.toScaleDp())
            ) {
                Row {
                    Text(
                        text = user?.fullName ?: "",
                        fontSize = 27.toScaleSp(),
                        lineHeight = 30.toScaleSp(),
                        color = Color.Black,
                        fontWeight = FontWeight.W400,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                    Text(
                        text = rolesMap.entries.find { role -> role.value == user?.role }?.key
                            ?: "",
                        fontSize = 16.toScaleSp(),
                        lineHeight = 30.toScaleSp(),
                        color = Color.Black,
                        fontWeight = FontWeight.Normal,
                        modifier = Modifier.padding(start = 20.toScaleDp()),
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                }
                Spacer(modifier = Modifier.height(20.toScaleDp()))
                Row(modifier = Modifier.width(400f.toScaleDp())) {
                    Column(
                        modifier = Modifier
                            .weight(1f)
                            .clickableWithPointer {
                                selectedIndex.value = 1
                            },
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        DisableSelection {
                            Text(
                                text = stringResource(Res.string.personal_details),
                                fontSize = 18.toScaleSp(),
                                lineHeight = 30.toScaleSp(),
                                color = Color.Black,
                                fontWeight = if (selectedIndex.value == 1) FontWeight.Bold else FontWeight.Normal,
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis
                            )
                        }
                        Spacer(modifier = Modifier.height(4.toScaleDp()))
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(if (selectedIndex.value == 1) 3.toScaleDp() else 1.toScaleDp())
                                .background(if (selectedIndex.value == 1) BlueApp else Color.LightGray)
                        )
                    }
                    Column(
                        modifier = Modifier
                            .weight(1f)
                            .clickableWithPointer {
                                selectedIndex.value = 2
                            },
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        DisableSelection {
                            Text(
                                text = stringResource(Res.string.working_hours),
                                fontSize = 18.toScaleSp(),
                                lineHeight = 30.toScaleSp(),
                                color = Color.Black,
                                fontWeight = if (selectedIndex.value == 2) FontWeight.Bold else FontWeight.Normal,
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis
                            )
                        }
                        Spacer(modifier = Modifier.height(4.toScaleDp()))
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(if (selectedIndex.value == 2) 3.toScaleDp() else 1.toScaleDp())
                                .background(if (selectedIndex.value == 2) BlueApp else Color.LightGray)
                        )
                    }
                }
                Spacer(modifier = Modifier.height(15.toScaleDp()))

                if (selectedIndex.value == 1) {
                    listOf(
                        stringResource(Res.string.full_name) to (user?.fullName ?: ""),
                        stringResource(Res.string.email_address) to (user?.email ?: ""),
                        stringResource(Res.string.phone) to (user?.phone ?: ""),
                        stringResource(Res.string.upcoming_leaves) to (user?.upcomingLeave ?: "")
                    ).forEach { (label, value) ->
                        Row(modifier = Modifier.fillMaxWidth()) {
                            Text(
                                text = label,
                                fontSize = 16.toScaleSp(),
                                lineHeight = 30.toScaleSp(),
                                color = Color.Gray,
                                fontWeight = FontWeight.Normal,
                                modifier = Modifier
                                    .weight(0.3f),
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis
                            )
                            Text(
                                text = value,
                                fontSize = 16.toScaleSp(),
                                lineHeight = 30.toScaleSp(),
                                color = Color.Black,
                                fontWeight = FontWeight.Normal,
                                modifier = Modifier
                                    .weight(0.7f),
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis
                            )
                        }
                        Spacer(modifier = Modifier.height(10.toScaleDp()))
                    }
                } else {
                    val workingHours: WorkingHours? = user?.workingHours
                    val workingHoursText = workingHours?.let {
                        val groupedSchedule = groupContiguousWorkingHours(it)
                        formatWorkingHoursText(groupedSchedule)
                    }
                    Text(
                        text = workingHoursText ?: stringResource(Res.string.no_hours_available),
                        fontSize = 16.toScaleSp(),
                        lineHeight = 30.toScaleSp(),
                        color = Color.Black,
                        fontWeight = FontWeight.Normal,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )

                }
            }
            Column(
                modifier = Modifier.weight(1f)
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth()
                ) {
                    ProfilePictureSection(
                        user?.getName() ?: "",
                        modifier = Modifier
                            .width(pictureSize)
                            .aspectRatio(1f)
                            .wrapContentWidth(Alignment.End)
                    )
                }
            }
        }
    }
}

@Composable
fun PricingBoxSubscription(
    filteredPlans: List<Plan>,
    plans: List<Plan>,
    selectedPlanId: MutableState<String?>,
    user: User?,
    onClick: (plan: Plan) -> Unit,
    workspacePlan: String?
) {
    val listState = rememberLazyListState()
    val coroutineScope = rememberCoroutineScope()
    val isAdmin = user?.role == Role.admin.name || user?.role == Role.superAdmin.name

    BoxWithConstraints(
        modifier = Modifier
            .fillMaxWidth()
            .padding(16.toScaleDp()).pointerInput(Unit) {
                awaitPointerEventScope {
                    while (true) {
                        val event = awaitPointerEvent()
                        val scrollDelta = event.changes.firstOrNull()?.scrollDelta?.y ?: 0f
                        if (event.keyboardModifiers.isShiftPressed) {
                            coroutineScope.launch {
                                listState.scrollBy(-scrollDelta * 10)
                            }
                            event.changes.forEach { it.consume() }
                        }
                    }
                }
            },
        contentAlignment = Alignment.Center
    ) {
        val cardWidth = 200.toScaleDp()
        val cardHeight = 270.toScaleDp()
        val spacing = 40
        val columns =
            (maxWidth.value.roundToInt() / (cardWidth.value.roundToInt() + spacing)).coerceAtLeast(1)
                .coerceAtMost(4)
        val cardModifier = Modifier.width(cardWidth).height(cardHeight)
        Column(
            modifier = Modifier.fillMaxWidth(),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(16.toScaleDp())
        ) {
            filteredPlans.chunked(columns).forEach { rowPlans ->
                Row {
                    rowPlans.forEach { plan ->
                        val isSelected = plan._id == selectedPlanId.value
                        val userPlan = plans.find { it._id == user?.planId }
                            ?: plans.find { it._id == workspacePlan }

                        val isCurrentPlan = userPlan?._id == plan._id
                        Box(
                            modifier = cardModifier
                        ) {
                            if (isCurrentPlan) {
                                val chosenPlanText = if (isAdmin) {
                                    stringResource(Res.string.you_chose)
                                } else {
                                    stringResource(Res.string.current_plan)
                                }
                                Box(
                                    modifier = Modifier
                                        .height(28.toScaleDp())
                                        .width(122.toScaleDp())
                                        .offset(y = (-15).toScaleDp(), x = (-10).toScaleDp())
                                        .align(Alignment.TopEnd)
                                        .zIndex(1f)
                                        .background(
                                            BlueApp,
                                            shape = RoundedCornerShape(13.toScaleDp())
                                        ),
                                    contentAlignment = Alignment.Center
                                ) {
                                    Text(
                                        text = chosenPlanText,
                                        fontSize = 10.toScaleSp(),
                                        lineHeight = 12.toScaleSp(),
                                        color = Color.White,
                                        fontWeight = FontWeight.Bold,
                                        textAlign = TextAlign.Center,
                                        maxLines = 1,
                                        overflow = TextOverflow.Ellipsis
                                    )
                                }
                            }
                            Box(
                                modifier = Modifier
                                    .background(
                                        color = Color(0xFFfafafa),
                                        shape = RoundedCornerShape(16.toScaleDp())
                                    )
                                    .border(
                                        width = 2.toScaleDp(),
                                        color = if (isSelected) BlueApp else Color.Transparent,
                                        shape = RoundedCornerShape(16.toScaleDp())
                                    )
                                    .clickableWithPointer {
                                        onClick(plan)
                                    }
                                    .padding(16.toScaleDp())
                            ) {
                                PricingOptionSubscription(
                                    plan = plan,
                                    isMobileView = false
                                )
                            }
                        }
                        Spacer(modifier = Modifier.width(16.toScaleDp()))
                    }
                }
            }
        }
    }
}

@Composable
fun PricingOptionSubscription(
    isMobileView: Boolean,
    plan: Plan
) {
    val features = listOf(
        "${plan.limitManagers} ${stringResource(Res.string.managers)} + " +
                "${plan.limitUsers} ${stringResource(Res.string.users)}",
        plan.getPlanSupport(currentLanguage.value.code).orEmpty(),
        plan.getStorage(currentLanguage.value.code).orEmpty()
    )

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(if (!isMobileView) 400.toScaleDp() else 400.toScaleDp())
    ) {
        Column(
            modifier = Modifier.fillMaxSize(),
            verticalArrangement = Arrangement.SpaceBetween,
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth(),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Text(
                    text = buildAnnotatedString {
                        withStyle(
                            style = SpanStyle(
                                fontSize = 24.toScaleSp(),
                                fontWeight = FontWeight.W700,
                                color = PopularPricingText
                            )
                        ) {
                            append(plan.getPriceString())
                        }

                        if (plan.price != 0.0) {
                            val period = stringResourcePlain(Res.string.month)
                            withStyle(
                                style = SpanStyle(
                                    fontSize = 17.toScaleSp(),
                                    fontWeight = FontWeight.W400,
                                    color = PopularPricingText
                                )
                            ) {
                                append(" /$period")
                            }
                        }
                    },
                    modifier = Modifier
                        .align(Alignment.Start),
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                Spacer(modifier = Modifier.height(20.toScaleDp()))
                Text(
                    text = plan.getName(currentLanguage.value.code).orEmpty(),
                    fontSize = 24.toScaleSp(),
                    fontWeight = FontWeight.W700,
                    color = PopularPricingText,
                    modifier = Modifier
                        .align(Alignment.Start),
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                Spacer(modifier = Modifier.height(15.toScaleDp()))
                features.forEach { feature ->
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .align(Alignment.Start),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Image(
                            painter = painterResource(Res.drawable.check_circle_1),
                            contentDescription = "Feature icon",
                            //tint = Color.White,
                            modifier = Modifier.size(20.toScaleDp())
                        )

                        Spacer(modifier = Modifier.width(10.toScaleDp()))

                        Text(
                            text = feature,
                            fontSize = 15.toScaleSp(),
                            lineHeight = 18.toScaleSp(),
                            color = PopularPricingText,
                            fontWeight = FontWeight.W400,
                            maxLines = 3,
                            overflow = TextOverflow.Ellipsis
                        )
                    }
                    Spacer(modifier = Modifier.height(20.toScaleDp()))
                }
            }
        }
    }
}
